import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ErrorMessageService } from 'src/app/shared/services/error-message.service';

export interface ChatMessage {
  content: string;
  isUser: boolean;
  timestamp: Date;
}

export interface ChatbotRequest {
  question: string;  // Correspond au backend Flask
  domain?: string;   // Nouveau: domaine (emprunteur/sante)
}

export interface ChatbotResponse {
  answer: string;   // Correspond au backend Flask
  status?: 'success' | 'error';  // Renvoyé par le proxy FastAPI
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}`;
  private chatbotEndpoint = `${this.apiUrl}/chatbot`;

  constructor(
    private http: HttpClient,
    private errorMessageService: ErrorMessageService
  ) {}

  /**
   * Get authentication headers with proper token handling
   */
private getHeaders() {
  const token = localStorage.getItem('access_token');
  console.log('🔑 Token found:', token ? `YES (${token.substring(0, 20)}...)` : 'NO TOKEN');
  
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
}

  /**
   * Send message to chatbot via FastAPI backend (which then calls Flask)
   */
  askQuestion(message: string, domain: string = 'emprunteur'): Observable<ChatbotResponse> {
    const payload: ChatbotRequest = { question: message, domain };
    
    console.log('🤖 Sending to chatbot:', this.chatbotEndpoint, payload);
    console.log('🔑 Auth headers:', this.getHeaders());
    
    // Configuration spécifique pour le chatbot avec timeout désactivé
    const options = {
      ...this.getHeaders(),
      timeout: 0 // Pas de timeout
    };
    
    return this.http.post<ChatbotResponse>(this.chatbotEndpoint, payload, options)
      .pipe(
        map(response => {
          console.log('📥 Raw chatbot response:', response);
          console.log('📥 Raw answer content:', response.answer);

          // Le proxy FastAPI renvoie HTTP 200 même en cas d'échec, avec status: 'error'.
          // Sans ce contrôle, "Chatbot indisponible" s'afficherait comme une vraie réponse.
          if (response.status === 'error') {
            throw new Error(response.answer || response.error || 'Le chatbot est momentanément indisponible.');
          }

          const cleanedAnswer = this.cleanHtmlTags(response.answer);
          console.log('🧹 Cleaned answer content:', cleanedAnswer);

          const finalResponse = {
            ...response,
            answer: cleanedAnswer
          };

          console.log('✅ Final processed response:', finalResponse);
          return finalResponse;
        }),
        // Arrow function obligatoire : passer `this.handleError` en référence perd le
        // binding de `this` et fait planter le service sur `this.errorMessageService`.
        catchError((error) => this.handleError(error))
      );
  }
  /**
   * Remove HTML tags from text
   */
  private cleanHtmlTags(text: string): string {
    if (!text) return '';
    
    return text.replace(/<[^>]*>/g, '')
               .replace(/&nbsp;/g, ' ')
               .replace(/&amp;/g, '&')
               .replace(/&lt;/g, '<')
               .replace(/&gt;/g, '>')
               .replace(/&quot;/g, '"')
               .trim();
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: unknown): Observable<never> {
    console.error('Chatbot service error:', error);

    return throwError(() => new Error(this.toUserMessage(error)));
  }

  /**
   * Traduit une erreur en message lisible par l'utilisateur.
   *
   * L'erreur peut arriver sous trois formes :
   *  - HttpErrorResponse brute (si l'errorInterceptor l'a laissée passer)
   *  - Error déjà déballée par l'errorInterceptor (cas le plus courant)
   *  - Error levée par le `map` ci-dessus quand le proxy répond status: 'error'
   */
  private toUserMessage(error: unknown): string {
    const isHttpError = error instanceof HttpErrorResponse;

    if (isHttpError) {
      // 504/408 : le proxy a bien attendu, mais le traitement a dépassé son
      // délai. Suggérer de cibler la question a du sens ici.
      if (error.status === 504 || error.status === 408) {
        return "L'analyse a pris trop de temps et la connexion a expiré. "
          + 'Reformulez votre question de façon plus ciblée (par exemple en précisant un assureur) puis réessayez.';
      }

      // 502/503 : le service est injoignable ou pas encore démarré. Cette
      // réponse arrive immédiatement, PAS après un délai — la présenter comme
      // un dépassement de temps envoie l'utilisateur (et l'exploitant) sur une
      // fausse piste. Reformuler la question n'y changera rien.
      if (error.status === 502 || error.status === 503) {
        return 'Le service de chat est momentanément indisponible. '
          + 'Il redémarre peut-être : réessayez dans une minute.';
      }

      const { detail } = this.errorMessageService.getErrorMessage(error);
      return detail;
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Une erreur est survenue lors de la communication avec le chatbot. Veuillez réessayer.';
  }
}